import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import { useAuth } from '@/hooks/useAuth';

export interface TicketSummary {
  queued: number;
  accepted: number;
  done: number;
  rejected: number;
  /** queued + accepted — every ticket still waiting on somebody. */
  open: number;
}

/**
 * How often the menu badge re-asks. Ten seconds is the promise the screens make
 * ("within ten seconds, without refreshing") and it is deliberately a named
 * constant: the list page polls on the same clock, and two numbers drifting
 * apart is how a badge ends up contradicting the page it points at.
 */
export const TICKET_POLL_MS = 10_000;

export const TICKET_SUMMARY_KEY = ['/api/v1/tickets/summary'] as const;

/**
 * The counts behind the sidebar badge.
 *
 * Polled rather than pushed: the finance queues have a WebSocket because a
 * deposit sitting unseen costs money by the minute, and tickets do not — ten
 * seconds of staleness is within the promise, and a second socket for this
 * would be a second thing to notice the death of.
 *
 * `enabled` follows the permission, so a role without `ticket:read` never asks
 * — an operator who cannot open the page must not generate a request every ten
 * seconds for a 403.
 */
export function useTicketSummary() {
  const { hasPermission } = useAuth();
  return useQuery<TicketSummary>(ticketSummaryQueryOptions(hasPermission('ticket:read')));
}

/**
 * The options that one query is built from, split out for the reason
 * `listQueryOptions` is: the ten-second promise is a CONTRACT, and it should be
 * assertable without mounting React Query and waiting ten real seconds for a
 * tick.
 */
export function ticketSummaryQueryOptions(
  allowed: boolean,
): UseQueryOptions<TicketSummary> {
  return {
    queryKey: TICKET_SUMMARY_KEY,
    queryFn: () => api.get<TicketSummary>('/api/v1/tickets/summary'),
    enabled: allowed,
    refetchInterval: TICKET_POLL_MS,
    // A hidden tab stops polling — the same rule `useListQuery` applies. The
    // refetch React Query does when the tab becomes visible again covers the gap.
    refetchIntervalInBackground: false,
  };
}
